import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Axios from 'axios';
import FuncionesExtras from './funciones_extras';
import alertify from 'alertifyjs';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import '../css/gestion-pacientes.css';

const estadoInicial = {
	id_doctor: '',
	telefono: '',
	nombre: '',
	nombre_tutor: '',
	apellido: '',
	cedula: '',
	fecha_nacimiento: '',
	correo_electronico: '',
	sexo: 'h',
	es_pediatrico: 'no',
	fotoPreview: null
};

function extraerMensajeError(error) {
	const data = error && error.response && error.response.data;
	if (data && data.message) return data.message;
	if (data && data.error) return data.error;
	return 'No se pudo guardar el paciente';
}

function PacienteAdmin() {
	const [doctores, setDoctores] = useState([]);
	const [cargandoDoctores, setCargandoDoctores] = useState(true);
	const [errorDoctores, setErrorDoctores] = useState('');
	const [guardando, setGuardando] = useState(false);
	const [form, setForm] = useState({ ...estadoInicial });
	const fotoInputRef = useRef(null);

	const lang = FuncionesExtras.lenguaje.paciente_admin || {};

	const cargarDoctores = useCallback(() => {
		setCargandoDoctores(true);
		setErrorDoctores('');

		Axios.get(`${FuncionesExtras.url_base}/api/doctores`)
			.then((res) => {
				const lista = Array.isArray(res.data) ? res.data : [];
				setDoctores(lista);
				setCargandoDoctores(false);

				if (lista.length === 0) {
					setErrorDoctores('No hay doctores registrados. Agregue un doctor antes de registrar pacientes.');
					return;
				}
				if (lista.length === 1) {
					setForm((prev) => ({ ...prev, id_doctor: String(lista[0].id) }));
				}
			})
			.catch((error) => {
				console.error('Error al cargar doctores:', error);
				setDoctores([]);
				setCargandoDoctores(false);
				setErrorDoctores('No se pudieron cargar los doctores. Verifique la conexión con el servidor.');
			});
	}, []);

	useEffect(() => {
		cargarDoctores();
	}, [cargarDoctores]);

	useEffect(() => {
		return () => {
			if (form.fotoPreview) {
				URL.revokeObjectURL(form.fotoPreview);
			}
		};
	}, [form.fotoPreview]);

	const actualizarCampo = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const limpiarFormulario = () => {
		if (form.fotoPreview) {
			URL.revokeObjectURL(form.fotoPreview);
		}
		if (fotoInputRef.current) {
			fotoInputRef.current.value = '';
		}
		const idDoctor = doctores.length === 1 ? String(doctores[0].id) : '';
		setForm({ ...estadoInicial, id_doctor: idDoctor });
	};

	const validarFormulario = () => {
		if (!form.nombre.trim()) {
			alertify.error('Indique el nombre del paciente');
			return false;
		}
		if (!form.apellido.trim()) {
			alertify.error('Indique el apellido del paciente');
			return false;
		}
		if (!form.telefono || !String(form.telefono).trim()) {
			alertify.error('Indique el número de teléfono');
			return false;
		}
		if (!form.id_doctor || form.id_doctor === 'seleccionar') {
			alertify.error('Seleccione el doctor que ingresa al paciente');
			return false;
		}
		if (form.es_pediatrico === 'si' && !form.nombre_tutor.trim()) {
			alertify.error('Indique el nombre del tutor para pacientes pediátricos');
			return false;
		}
		if (form.correo_electronico.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo_electronico.trim())) {
			alertify.error('El correo electrónico no es válido');
			return false;
		}
		return true;
	};

	const guardarPaciente = (event) => {
		event.preventDefault();
		if (guardando || !validarFormulario()) return;

		setGuardando(true);

		const formData = new FormData();
		const inputFoto = fotoInputRef.current;
		if (inputFoto && inputFoto.files && inputFoto.files.length > 0) {
			formData.append('foto_paciente', inputFoto.files[0]);
		}

		formData.append('nombre', form.nombre.trim());
		formData.append('apellido', form.apellido.trim());
		formData.append('cedula', form.cedula.trim());
		formData.append('telefono', String(form.telefono));
		formData.append('id_doctor', form.id_doctor);
		formData.append('nombre_tutor', form.es_pediatrico === 'si' ? form.nombre_tutor.trim() : '');
		formData.append('fecha_nacimiento', form.fecha_nacimiento || '');
		formData.append('correo_electronico', form.correo_electronico.trim());
		formData.append('sexo', form.sexo || 'h');

		// No establecer Content-Type: axios debe enviar el boundary automáticamente
		Axios.post(`${FuncionesExtras.url_base}/api/guardar_paciente`, formData)
			.then((res) => {
				const data = res.data || {};
				alertify.success(data.message || 'Paciente registrado correctamente');
				limpiarFormulario();
			})
			.catch((error) => {
				alertify.error(extraerMensajeError(error));
				console.error(error);
			})
			.finally(() => {
				setGuardando(false);
			});
	};

	const cambiarFoto = (event) => {
		const file = event.target.files && event.target.files[0];
		setForm((prev) => {
			if (prev.fotoPreview) {
				URL.revokeObjectURL(prev.fotoPreview);
			}
			if (!file) {
				return { ...prev, fotoPreview: null };
			}
			return { ...prev, fotoPreview: URL.createObjectURL(file) };
		});
	};

	const abrirSelectorFoto = () => {
		if (fotoInputRef.current) {
			fotoInputRef.current.click();
		}
	};

	const iniciales = `${(form.nombre || '?').charAt(0)}${(form.apellido || '').charAt(0)}`.toUpperCase();

	const renderField = (id, label, optional, children) => (
		<div className="col-md-6 agregar-paciente-field">
			<label htmlFor={id} className="agregar-paciente-label">
				{label}
				{optional && <span className="agregar-paciente-label-optional"> (opcional)</span>}
			</label>
			{children}
		</div>
	);

	return (
		<div className="col-12 col-md-10 listado-pacientes-page agregar-paciente-page">
			<div className="listado-pacientes-hero">
				<div className="listado-pacientes-hero-info">
					<div className="listado-pacientes-hero-icon">
						<i className="fas fa-user-plus" />
					</div>
					<div>
						<h1 className="listado-pacientes-hero-title">
							{FuncionesExtras.lenguaje.agregar_paciente || 'Agregar paciente'}
						</h1>
						<p className="listado-pacientes-hero-subtitle">
							Complete los datos para registrar un nuevo paciente en el sistema
						</p>
						<div className="agregar-paciente-nav">
							<Link to="/pacientes_registrados" className="agregar-paciente-nav-link agregar-paciente-nav-link--ghost">
								<i className="fas fa-list" />
								Pacientes registrados
							</Link>
							<Link to="/cargar_pacientes" className="agregar-paciente-nav-link agregar-paciente-nav-link--primary">
								<i className="fas fa-calendar-check" />
								Pacientes / Citas
							</Link>
							{errorDoctores && (
								<Link to="/doctor" className="agregar-paciente-nav-link agregar-paciente-nav-link--ghost">
									<i className="fas fa-user-md" />
									Agregar doctor
								</Link>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="agregar-paciente-form-card">
				<form className="agregar-paciente-form-body" onSubmit={guardarPaciente} noValidate>
					{errorDoctores && (
						<div className="agregar-paciente-alert agregar-paciente-alert--warning">
							<i className="fas fa-exclamation-triangle" />
							<span>{errorDoctores}</span>
						</div>
					)}

					<div className="agregar-paciente-section">
						<h2 className="agregar-paciente-section-title">
							<i className="fas fa-id-card" />
							Datos personales
						</h2>
						<div className="row">
							{renderField('nombre', lang.nombre || 'Nombre', false,
								<input
									type="text"
									id="nombre"
									name="nombre"
									className="agregar-paciente-input form-control"
									value={form.nombre}
									onChange={actualizarCampo}
									autoComplete="given-name"
									required
								/>
							)}
							{renderField('apellido', lang.apellido || 'Apellido', false,
								<input
									type="text"
									id="apellido"
									name="apellido"
									className="agregar-paciente-input form-control"
									value={form.apellido}
									onChange={actualizarCampo}
									autoComplete="family-name"
									required
								/>
							)}
							{renderField('cedula', lang.cedula || 'Cédula', true,
								<input
									type="text"
									id="cedula"
									name="cedula"
									className="agregar-paciente-input form-control"
									value={form.cedula}
									onChange={actualizarCampo}
								/>
							)}
							{renderField('sexo', lang.sexo || 'Sexo', false,
								<select
									id="sexo"
									name="sexo"
									className="agregar-paciente-select form-control"
									value={form.sexo}
									onChange={actualizarCampo}
								>
									<option value="h">Masculino</option>
									<option value="m">Femenino</option>
								</select>
							)}
							{renderField('fecha_nacimiento', lang.fecha_de_nacimiento || 'Fecha de nacimiento', true,
								<input
									type="date"
									id="fecha_nacimiento"
									name="fecha_nacimiento"
									className="agregar-paciente-input form-control"
									value={form.fecha_nacimiento}
									onChange={actualizarCampo}
								/>
							)}
						</div>

						<div className="agregar-paciente-pediatrico">
							<div className="agregar-paciente-pediatrico-header">
								<span className="agregar-paciente-label" style={{ marginBottom: 0 }}>
									<i className="fas fa-child" style={{ color: '#667eea', marginRight: 8 }} />
									¿Es pediátrico?
								</span>
								<label className="agregar-paciente-toggle">
									<input
										type="checkbox"
										id="es_pediatrico"
										checked={form.es_pediatrico === 'si'}
										onChange={(ev) => setForm((prev) => ({
											...prev,
											es_pediatrico: ev.target.checked ? 'si' : 'no',
											nombre_tutor: ev.target.checked ? prev.nombre_tutor : ''
										}))}
									/>
									<span className="agregar-paciente-toggle-track" />
									<span className="agregar-paciente-toggle-thumb" />
								</label>
							</div>
							{form.es_pediatrico === 'si' && (
								<div className="agregar-paciente-field" style={{ marginTop: 16, marginBottom: 0 }}>
									<label htmlFor="nombre_tutor" className="agregar-paciente-label">
										Nombre del tutor *
									</label>
									<input
										type="text"
										id="nombre_tutor"
										name="nombre_tutor"
										className="agregar-paciente-input form-control"
										placeholder="Nombre completo del tutor"
										value={form.nombre_tutor}
										onChange={actualizarCampo}
									/>
								</div>
							)}
						</div>
					</div>

					<div className="agregar-paciente-section">
						<h2 className="agregar-paciente-section-title">
							<i className="fas fa-address-book" />
							Contacto
						</h2>
						<div className="row">
							<div className="col-md-6 agregar-paciente-field">
								<label className="agregar-paciente-label" htmlFor="telefono_paciente">
									{lang.telefono || 'Número de teléfono'} *
								</label>
								<div className="agregar-paciente-phone">
									<PhoneInput
										id="telefono_paciente"
										defaultCountry="DO"
										placeholder="Ingrese el número de teléfono"
										value={form.telefono}
										onChange={(valor) => setForm((prev) => ({ ...prev, telefono: valor || '' }))}
										numberInputProps={{ className: 'PhoneInputInput form-control' }}
									/>
								</div>
							</div>
							{renderField('correo_electronico', lang.correo_electronico || 'Correo electrónico', true,
								<input
									type="email"
									id="correo_electronico"
									name="correo_electronico"
									className="agregar-paciente-input form-control"
									placeholder="ejemplo@correo.com"
									value={form.correo_electronico}
									onChange={actualizarCampo}
									autoComplete="email"
								/>
							)}
						</div>
					</div>

					<div className="agregar-paciente-section">
						<h2 className="agregar-paciente-section-title">
							<i className="fas fa-camera" />
							Foto del paciente
						</h2>
						<div
							className="agregar-paciente-foto-zone"
							onClick={abrirSelectorFoto}
							onKeyDown={(ev) => ev.key === 'Enter' && abrirSelectorFoto()}
							role="button"
							tabIndex={0}
						>
							{form.fotoPreview ? (
								<img src={form.fotoPreview} alt="Vista previa" className="agregar-paciente-foto-preview" />
							) : (
								<div className="agregar-paciente-foto-placeholder">
									{iniciales.length > 1 ? iniciales : <i className="fas fa-user" />}
								</div>
							)}
							<div className="agregar-paciente-foto-text">
								<strong>{form.fotoPreview ? 'Cambiar foto' : 'Subir foto del paciente'}</strong>
								<span>Haga clic para seleccionar una imagen (JPG, PNG)</span>
							</div>
							<input
								type="file"
								id="foto_paciente"
								ref={fotoInputRef}
								accept="image/*"
								className="agregar-paciente-foto-input"
								onChange={cambiarFoto}
							/>
						</div>
					</div>

					<div className="agregar-paciente-section">
						<h2 className="agregar-paciente-section-title">
							<i className="fas fa-user-md" />
							Asignación médica
						</h2>
						<div className="row">
							<div className="col-md-12 agregar-paciente-field">
								<label htmlFor="doctores_select" className="agregar-paciente-label">
									{lang.ingresado || 'Ingresado por doctor'} *
								</label>
								<select
									id="doctores_select"
									className="agregar-paciente-select form-control"
									value={form.id_doctor || 'seleccionar'}
									onChange={(ev) => setForm((prev) => ({ ...prev, id_doctor: ev.target.value }))}
									disabled={cargandoDoctores || doctores.length === 0}
								>
									<option value="seleccionar">
										{cargandoDoctores ? 'Cargando doctores…' : 'Seleccione el doctor'}
									</option>
									{doctores.map((doctor) => (
										<option key={doctor.id} value={doctor.id}>
											{doctor.nombre} {doctor.apellido}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>

					<div className="agregar-paciente-actions">
						<button
							type="button"
							className="agregar-paciente-btn agregar-paciente-btn--ghost"
							onClick={limpiarFormulario}
							disabled={guardando}
						>
							<i className="fas fa-eraser" />
							Limpiar
						</button>
						<button
							type="submit"
							className="agregar-paciente-btn agregar-paciente-btn--primary"
							disabled={guardando || cargandoDoctores}
						>
							{guardando ? (
								<><i className="fas fa-spinner fa-spin" /> Guardando…</>
							) : (
								<><i className="fas fa-save" /> Guardar paciente</>
							)}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default PacienteAdmin;
